import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useGameDataContext } from "@context/GameDataContext";
import { Alert, Box, Divider } from "@mui/material";
import RefereeTabletHeader from "../components/referee-tablet-page/RefereeTabletHeader";
import RefereeTabletLineUp from "../components/referee-tablet-page/RefereeTabletLineUp";
import RefereeTabletSanctions from "../components/referee-tablet-page/RefereeTabletSanctions";
import RefereeTabletRemainingTable from "../components/referee-tablet-page/RefereeTabletRemainingTable";
import RefereeTabletStatusMessage from "../components/referee-tablet-page/status-message/RefereeTabletStatusMessage";
import { GameProgress } from "@/models/gameState";
import RefereeTabletLicenseConfirmedProgress from "../components/referee-tablet-page/RefereeTabletLicenseConfirmedProgress";
import RefereeTabletPregameTeamStats from "../components/referee-tablet-page/RefereeTabletPregameTeamStats";
import { gameState } from "@/services/gameState";
import RefereeTabletActionButtons from "../components/referee-tablet-page/RefereeTabletActionButtons";
import RefereeTabletSetStats from "../components/referee-tablet-page/RefereeTabletSetStats";
import { SamsScope, samsScopes } from "@/models/samsVbl";
import RefereeTabletChallengeInfo from "../components/referee-tablet-page/RefereeTabletChallengeInfo";
import RefereeTabletFunctionsBar from "@components/referee-tablet-page/RefereeTabletFunctionsBar";
import { useTabManagement } from "@context/TabManagementContext";
import { useAppDataContext } from "@context/AppContext";
import PaddedDivider from "@components/layout/PaddedDivider";
import RefereeTabletIneligibleList from "@components/referee-tablet-page/RefereeTabletIneligibleList";

const GameRefereeTabletView: FC = () => {
  let { samsId, scope } = useParams();
  const { game, gameLoadingState, useGame } = useGameDataContext();
  const { attemptAlwaysOnScreen } = useAppDataContext();
  const { setRequestWakeLock } = useTabManagement();

  useEffect(() => {
    setRequestWakeLock(attemptAlwaysOnScreen);
    return () => setRequestWakeLock(false);
  }, []);

  useEffect(() => {
    if (samsId && samsScopes.includes(scope as SamsScope)) {
      if (!game || game.samsId !== samsId) {
        useGame(samsId, scope as SamsScope);
      } else {
        console.debug("SamsId already correctly set", samsId, game.samsId);
      }
    } else {
      console.error("NO SAMS ID");
    }
  }, [samsId, game, scope]);

  if (!samsScopes.includes(scope as SamsScope)) {
    return <Alert severity={"error"}>Scope is invalid</Alert>;
  } else if (!game) {
    if (gameLoadingState === "loading") {
      return <Alert severity="info">Das Spiel wird geladen...</Alert>;
    } else {
      return (
        <Alert severity="warning">
          Es konnte kein Spiel mit ID {samsId} gefunden werden!
        </Alert>
      );
    }
  } else {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          flexWrap: "nowrap",
          overflow: "clip",
          justifyContent: "center",
        }}
      >
        <RefereeTabletHeader />
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
            minHeight: "2em",
            display: "flex",
            justifyContent: "flex-start",
            px: 1,
            maxWidth: "100vw",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            gap: "2vw",
          }}
        >
          <LineupOrLicenseConfirmedProgress />
          <RefereeTabletStatusMessage />
          <RemainingOrPregameTeamStats />
          <PaddedDivider />
          <RefereeTabletSanctions />
          <PaddedDivider />
          <RefereeTabletIneligibleList />
          <RefereeTabletSetStats />
          <PaddedDivider />
          <RefereeTabletFunctionsBar />
        </Box>
        <RefereeTabletActionButtons />
      </Box>
    );
  }
};

const LineupOrLicenseConfirmedProgress: FC = () => {
  const gameProgress = gameState("gameProgress");

  const [showLicenseConfirmedProgress, setShowLicenseConfirmedProgress] =
    useState(false);
  useEffect(() => {
    if (gameProgress !== GameProgress.CREATED) {
      setShowLicenseConfirmedProgress(
        gameProgress.valueOf() < GameProgress.ARBITRATION_CONFIRMED,
      );
    }
  }, [gameProgress]);

  return (
    <Box>
      {showLicenseConfirmedProgress ? (
        <RefereeTabletLicenseConfirmedProgress />
      ) : (
        <RefereeTabletLineUp />
      )}
    </Box>
  );
};

const RemainingOrPregameTeamStats: FC = () => {
  const gameProgress = gameState("gameProgress");

  const [showPregameTeamStats, setShowPregameTeamStats] = useState(false);

  useEffect(() => {
    if (gameProgress !== GameProgress.CREATED) {
      setShowPregameTeamStats(
        gameProgress.valueOf() < GameProgress.GAME_STARTED,
      );
    }
  }, [gameProgress]);

  return showPregameTeamStats ? (
    <RefereeTabletPregameTeamStats />
  ) : (
    <Box
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: "3fr 4fr 3fr",
        justifyContent: "center",
        fontSize: "5vw",
      }}
    >
      <RefereeTabletChallengeInfo side={"left"} />
      <RefereeTabletRemainingTable />
      <RefereeTabletChallengeInfo side={"right"} />
    </Box>
  );
};

export default GameRefereeTabletView;
